/**
 * Recording recovery hook.
 *
 * Handles the RECOVERY_STOP_RECORDING_EVENT — when the RecoveryProvider
 * detects the user returned after the app was backgrounded, this hook
 * force-stops the recording and salvages chunks from IndexedDB.
 */

import { useEffect } from 'react'
import { useStore } from '@/stores'
import { getChunksBySession, saveDraft } from '@/lib/db'
import { useToast } from '@/components/providers/ToastProvider'
import { getLocalTimeString } from '@/lib/time'
import type { AudioConsentMetadata, DraftRecording } from '@/types'
import { useAuthStateSnapshot } from '@/lib/authStatus'
import { normalizeTaxonomySelection } from '@/lib/taxonomySelection'

const RECOVERY_STOP_RECORDING_EVENT = 'app-recovery-stop-recording'

interface UseRecordingRecoveryOptions {
  isRecording: boolean
  stopRecording: () => void
  getSessionId: () => string | null
  getLastStoppedSessionId: () => string | null
  pendingConsentRef: React.MutableRefObject<AudioConsentMetadata | null>
  forcedStopPendingRef: React.MutableRefObject<boolean>
}

export function useRecordingRecovery(options: UseRecordingRecoveryOptions) {
  const { forcedStopPendingRef } = options
  const selectedAssetId = useStore((state) => state.selectedAssetId)
  const selectedIndicationId = useStore((state) => state.selectedIndicationId)
  const selectedKiqIds = useStore((state) => state.selectedKiqIds)
  const selectedInsightTags = useStore((state) => state.selectedInsightTags)
  const setDraft = useStore((state) => state.setDraft)
  const { showToast } = useToast()
  const authState = useAuthStateSnapshot()

  useEffect(() => {
    const handleForceStop = () => {
      if (options.isRecording) {
        forcedStopPendingRef.current = true
        options.stopRecording()
        showToast('Recording stopped to keep your data safe after returning to the app.', 'warning')

        const handleSaved = () => {
          forcedStopPendingRef.current = false
          window.removeEventListener('recording-saved', handleSaved)
        }
        window.addEventListener('recording-saved', handleSaved, { once: true })

        window.setTimeout(async () => {
          if (!forcedStopPendingRef.current) {
            return
          }
          const sessionId = options.getSessionId() ?? options.getLastStoppedSessionId()
          if (!sessionId) {
            return
          }
          try {
            const chunks = await getChunksBySession(sessionId)
            if (chunks.length === 0) {
              return
            }
            const totalDuration = chunks.reduce((sum, chunk) => sum + (chunk.durationMs || 0), 0)
            const chunkIds = chunks.map((chunk) => chunk.id)
            const chunkMimeType = chunks[0]?.blobType || chunks[0]?.blob?.type || 'audio/webm'
            const taxonomySelection = normalizeTaxonomySelection({
              assetId: selectedAssetId,
              indicationId: selectedIndicationId,
              kiqIds: selectedKiqIds,
            })
            const draft: DraftRecording = {
              id: sessionId,
              userId: authState.userSub,
              assetId: taxonomySelection.assetId,
              indicationId: taxonomySelection.indicationId,
              kiqIds: taxonomySelection.kiqIds,
              insightTags: selectedInsightTags,
              blob: new Blob([], { type: chunkMimeType }),
              durationMs: totalDuration,
              createdAt: getLocalTimeString(),
              chunkSessionId: sessionId,
              chunkIds,
              chunkCount: chunks.length,
              participants: options.pendingConsentRef.current?.participants,
              recordingWithoutParticipants: options.pendingConsentRef.current?.recordingWithoutParticipants,
              consentDescription: options.pendingConsentRef.current?.consentDescription,
              consentConfirmed: options.pendingConsentRef.current?.consentConfirmed,
            }
            await saveDraft(draft)
            setDraft(draft)
            forcedStopPendingRef.current = false
            showToast('Recording saved as draft', 'success')
            window.dispatchEvent(new Event('recording-saved'))
          } catch (error) {
            console.warn('Failed to salvage recording after forced stop', error)
          }
        }, 500)
      }
    }
    window.addEventListener(RECOVERY_STOP_RECORDING_EVENT, handleForceStop)
    return () => {
      window.removeEventListener(RECOVERY_STOP_RECORDING_EVENT, handleForceStop)
    }
  }, [
    authState.userSub,
    options.isRecording,
    options.stopRecording,
    options.getSessionId,
    options.getLastStoppedSessionId,
    selectedAssetId,
    selectedIndicationId,
    selectedKiqIds,
    selectedInsightTags,
    setDraft,
    showToast,
  ])

}

